import "./CategoryAccordion.scss";
import { useCallback, ReactNode, Fragment, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppSelector, useAppDispatch } from "../../../store";
import { toggleCategoryExpanded, setCategoryExpanded, setSideNavExpanded } from "../../../reducers/uiState";
import { IconChevronDown } from "../../../common/icons/IconChevronDown/IconChevronDown";
import { isRouteActive } from "../../../common/util/Navigation/routeMatching";
import defaultContent from "../../../content/navigation";
import defaultCommonContent from "../../../content/common";

export interface AccordionMenuItem {
  id: string;
  contentKey?: string;
  label: string;
  path?: string;
  children?: AccordionMenuItem[];
  isSection?: boolean;
}

/**
 * CategoryAccordion - Expandable navigation category component
 *
 * This component displays a collapsible navigation category with menu items.
 * The expanded state is managed via Redux and persisted to localStorage across sessions.
 */
interface CategoryAccordionProps {
  categoryId: string; // Unique identifier for this category (e.g., "performanceIncome")
  title: string;
  items: AccordionMenuItem[];
  icon: ReactNode;
  defaultExpanded?: boolean; // Deprecated - ignored in favor of persisted Redux state
  isActive?: boolean; // Whether this category contains the current active route
}

export const CategoryAccordion = ({
  categoryId,
  title,
  items,
  icon,
  defaultExpanded: _defaultExpanded = false,
  isActive = false,
}: CategoryAccordionProps) => {
  const { t } = useTranslation(["navigation", "common"]);
  const dispatch = useAppDispatch();

  const disclaimerSymbol = t("disclaimerSymbol", defaultCommonContent["disclaimerSymbol"], { ns: "common" });

  // Read from Redux instead of local state
  const expandedCategories = useAppSelector((state) => state.uiState.expandedCategories);
  const isExpanded = expandedCategories.includes(categoryId);

  const { pathname, hash } = useLocation();

  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isExpanded && rootRef.current?.scrollIntoView) {
      const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      rootRef.current.scrollIntoView({ behavior: reducedMotion ? "instant" : "smooth", block: "start" });
    }
  }, [isExpanded]);

  const handleToggle = useCallback(() => {
    dispatch(toggleCategoryExpanded(categoryId));
  }, [dispatch, categoryId]);

  const handleItemClick = useCallback(() => {
    // Collapse the accordion after navigation (idempotent - safe for rapid clicks)
    dispatch(setCategoryExpanded({ categoryId, expanded: false }));
    // Collapse the side navigation to show highlighted icon
    dispatch(setSideNavExpanded(false));
  }, [dispatch, categoryId]);

  const renderMenuItem = (item: AccordionMenuItem, isSubItem = false) => {
    const linkID = item.contentKey ?? item.id;

    if (item.isSection) {
      return (
        <Fragment key={item.id}>
          <div className="CategoryAccordion__section-header">
            {t(
              `linkTitle.${linkID}`,
              defaultContent.linkTitle[linkID as keyof typeof defaultContent.linkTitle] || item.label,
            ).toString()}
          </div>
          {item.children && item.children.length > 0 && (
            <>{item.children.map((child) => renderMenuItem(child, true))}</>
          )}
        </Fragment>
      );
    }

    const isItemActive = isRouteActive(item.path, pathname, hash);
    const translatedLabel = t(
      `linkTitle.${linkID}`,
      defaultContent.linkTitle[linkID as keyof typeof defaultContent.linkTitle] || item.label,
      { symbol: disclaimerSymbol },
    );

    const [itemPathname, itemHash] = (item.path ?? "").split("#");

    return (
      <Link
        key={item.id}
        to={{ pathname: itemPathname, hash: itemHash ? `#${itemHash}` : undefined }}
        className={`CategoryAccordion__item ${isSubItem ? "CategoryAccordion__item--sub" : ""} ${isItemActive ? "CategoryAccordion__item--active" : ""}`}
        data-testid={`accordion-item-${item.id}`}
        onClick={handleItemClick}
      >
        {translatedLabel}
      </Link>
    );
  };

  return (
    <div className="CategoryAccordion" ref={rootRef}>
      <button
        type="button"
        className={`CategoryAccordion__header ${isActive ? "CategoryAccordion__header--active" : ""}`}
        onClick={handleToggle}
        aria-expanded={isExpanded}
        aria-label={`${title} menu`}
        data-testid={`accordion-header-${categoryId}`}
      >
        <div className="CategoryAccordion__header-icon">{icon}</div>
        <span className="CategoryAccordion__header-title">{title}</span>
        <div
          className={`CategoryAccordion__header-chevron ${isExpanded ? "CategoryAccordion__header-chevron--expanded" : ""}`}
        >
          <IconChevronDown />
        </div>
      </button>

      {isExpanded && (
        <div className="CategoryAccordion__content">
          <div className="CategoryAccordion__items">{items.map((item) => renderMenuItem(item))}</div>
        </div>
      )}
    </div>
  );
};
